'use client'

import { useState } from 'react'

import {
  Button,
  CenteredLayout,
  Input,
  LoginBox,
} from '@mntn-dev/auth-components'
import { useFlags } from '@mntn-dev/flags-client'
import { useTranslation } from '@mntn-dev/i18n'

import { getAssetUrl } from '@mntn-dev/app-assets'
import { useQueryParams } from '@mntn-dev/app-navigation'
import { useForm, zodResolver } from '@mntn-dev/forms'
import { trpcReactClient } from '~/app/_trpc/trpc-react-client.ts'
import { NextLink } from '~/components/link/next-link.tsx'
import { formSchema, onSubmit } from './form-handler.ts'

const LoginPage = () => {
  const flags = useFlags()
  const { t } = useTranslation('login')
  const { t: tValidation } = useTranslation('validation')
  const trpcUtils = trpcReactClient.useUtils()
  const [loading, setLoading] = useState(false)
  const { emailAddress, returnTo } = useQueryParams<'/login'>()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(
      formSchema({
        restrictedLogins: flags.magicskyRestrictedLogins,
        emailErrorMessage: t('unsupported-email'),
      }),
      tValidation
    ),
    defaultValues: {
      emailAddress,
      returnTo,
    },
  })

  const handleSubmitClick = (data: { emailAddress: string }) => {
    setLoading(true)
    onSubmit(trpcUtils.client, data).catch(() => {
      setLoading(false)
      setError(
        'emailAddress',
        { type: 'focus', message: t('unavailable') },
        { shouldFocus: true }
      )
    })
  }

  return (
    <CenteredLayout background="default">
      <LoginBox>
        <LoginBox.Header
          image={{
            url: getAssetUrl('quickframe-logo'),
            alt: t('alt'),
          }}
          title={t('title')}
          subtitle={t('subtitle')}
        />
        <LoginBox.Body>
          <LoginBox.Form onSubmit={handleSubmit(handleSubmitClick)}>
            <LoginBox.FormField>
              <Input
                label={t('email')}
                id="emailAddress"
                initialFilled={!!emailAddress}
                hasError={!!errors.emailAddress}
                {...register('emailAddress')}
              />
              {errors.emailAddress && (
                <LoginBox.FormFieldError>
                  {errors?.emailAddress.message ?? ''}
                </LoginBox.FormFieldError>
              )}
            </LoginBox.FormField>
            <LoginBox.FormFooter
              signUp={t('sign-up')}
              haveAccount={t('have-account')}
              Link={NextLink}
            >
              <Button loading={loading} type="submit">
                {t('continue')}
              </Button>
            </LoginBox.FormFooter>
          </LoginBox.Form>
        </LoginBox.Body>
      </LoginBox>
    </CenteredLayout>
  )
}

export default LoginPage
